// ─────────────────────────────────────────────────────────────────────────────
// PriceObservationsEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for the price observations.
// Responsibilities:
//   - Load the paginated observations list and the import journal
//   - Update, delete and bulk-delete observations, settle a conflict
//   - Manage loading state via SetPriceObservationsLoader
//   - Show toast notifications on success/error
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';
import { CommonService } from 'src/app/shared/common.service';
import { PriceCandidatesStore } from '../../quotations/services/price-candidates.store';
import { PriceObservationService } from '../services/price-observation.service';
import { IPriceImport, IPriceObservation } from '../models/price-observation.models';
import * as priceObservationsActions from './price-observations.actions';

@Injectable()
export class PriceObservationsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private priceObservationService: PriceObservationService,
    private priceCandidates: PriceCandidatesStore,
    private commonService: CommonService,
    private translateService: TranslateService,
    private errorMessages: ErrorMessageService,
  ) {}

  // ── Cache invalidation ───────────────────────────────────────────────────
  // Every write in this tab changes what the pricing engine answers, and the
  // order screens memoize that answer for the session. One effect on the
  // success actions, rather than a call in each dialog, so the next write
  // action added to this tab is covered by construction.

  InvalidatePriceCandidates$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          priceObservationsActions.UpdatePriceObservationSuccess,
          priceObservationsActions.KeepPriceObservationSuccess,
          priceObservationsActions.DeletePriceObservationSuccess,
          priceObservationsActions.BulkDeletePriceObservationsSuccess,
        ),
        tap(() => this.priceCandidates.invalidate()),
      ),
    { dispatch: false },
  );

  // ── Load observations ────────────────────────────────────────────────────

  LoadPriceObservations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.LoadPriceObservations),
      switchMap((action) =>
        this.priceObservationService.listObservations(action.page, action.key, action.filters, action.sortBy, action.sortMode).pipe(
          map((res: APIResponse<IPriceObservation>) => priceObservationsActions.LoadPriceObservationsSuccess({ response: res })),
          catchError(() => of(priceObservationsActions.LoadPriceObservationsError())),
        ),
      ),
    ),
  );

  // ── Load the import journal ──────────────────────────────────────────────

  LoadPriceImports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.LoadPriceImports),
      switchMap((action) =>
        this.priceObservationService.listImports(action.page, action.state).pipe(
          map((res: APIResponse<IPriceImport>) => priceObservationsActions.LoadPriceImportsSuccess({ response: res })),
          catchError(() => of(priceObservationsActions.LoadPriceImportsError())),
        ),
      ),
    ),
  );

  // ── Update ───────────────────────────────────────────────────────────────

  UpdatePriceObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.UpdatePriceObservation),
      switchMap((action) =>
        this.priceObservationService.updateObservation(action.id, action.payload).pipe(
          map((res: APIResponse<IPriceObservation>) =>
            priceObservationsActions.UpdatePriceObservationSuccess({ observation: this.single(res) }),
          ),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.price_observations.toast.update_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(priceObservationsActions.UpdatePriceObservationError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Keep this price ──────────────────────────────────────────────────────
  // Settles a conflict server-side: the kept row is served, the rival lands
  // superseded. The whole list reloads on success — the rival row changed too,
  // and a patch-in-place would leave it stale on screen.

  KeepPrice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.KeepPriceObservation),
      switchMap((action) =>
        this.priceObservationService.keepPrice(action.id).pipe(
          map((res: APIResponse<IPriceObservation>) =>
            priceObservationsActions.KeepPriceObservationSuccess({ observation: this.single(res) }),
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('knowledge_base.price_observations.toast.price_kept'),
              'SUCCESS',
            );
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            this.commonService.showToast(message, 'ERROR');
            return of(priceObservationsActions.KeepPriceObservationError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Delete ───────────────────────────────────────────────────────────────

  DeletePriceObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.DeletePriceObservation),
      switchMap((action) =>
        this.priceObservationService.deleteObservation(action.id).pipe(
          map(() => priceObservationsActions.DeletePriceObservationSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.price_observations.toast.delete_success'), 'SUCCESS');
          }),
          catchError(() => of(priceObservationsActions.DeletePriceObservationError())),
        ),
      ),
    ),
  );

  BulkDeletePriceObservations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(priceObservationsActions.BulkDeletePriceObservations),
      tap(() => {
        this.store.dispatch(priceObservationsActions.SetPriceObservationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.priceObservationService.bulkDeleteObservations(action.ids).pipe(
          map(() => priceObservationsActions.BulkDeletePriceObservationsSuccess()),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('knowledge_base.price_observations.toast.bulk_delete_success'),
              'SUCCESS',
            );
          }),
          catchError(() => of(priceObservationsActions.BulkDeletePriceObservationsError())),
          finalize(() => {
            this.store.dispatch(priceObservationsActions.SetPriceObservationsLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Helpers ──────────────────────────────────────────────────────────────

  /**
   * The single record of a write response.
   *
   * ``success_response`` puts a dict in ``object`` and a list in ``data``:
   * these routes answer with one record, so it lands in ``object``. The
   * ``data[0]`` fallback keeps this working if a route is ever switched to the
   * list envelope.
   */
  private single(res: APIResponse<IPriceObservation>): IPriceObservation {
    return (res.object ?? res.data?.[0]) as IPriceObservation;
  }

  /**
   * The one message the toast shows.
   *
   * Extraction is delegated to {@link ErrorMessageService}, the single place
   * that knows the backend's two error envelopes and that substitutes a
   * readable text for the failures that never carried a body (offline, 5xx)
   * instead of echoing "Http failure response for ...".
   */
  private resolveErrorMessage(error: HttpErrorResponse): string {
    const { messages } = this.errorMessages.parse(error);
    const message = Array.isArray(messages) ? messages.join(' ') : messages;
    return message || this.translateService.instant('knowledge_base.price_observations.toast.error');
  }
}
